// Libs
import type { VFC } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { Tsg } from '@sparky/framework'
import { ToastProvider } from '@panwds/react-ui'

// Services
import IAMService from '../iam/services/IAMService'

// Context
import { useMessage } from '../common/providers/MessageProvider'
import LoadingProvider, { useLoadingContext } from '../common/context/LoadingContext'
import GlobalContext from '../common/context/GlobalContext'
import IpRestrictProvider, { useIpRestrictionContext } from '../common/context/IpRestrictContext'
import IPRestrictionWrapper from '../common/components/noAccess/IPRestrictionWrapper'

// Components
import IpAllowBanner from './components/IpAllowBanner'
import TrustedIpForm from './components/TrustedIpForm'
import NoDataScreen from '../common/components/noDataScreen/NoDataScreen'
import RCTree from '../common/components/page/rcTree/RCTree'

// Actions
import { setAllowedIps, setTrustedIPLimit } from '../common/reducers/actions/IAMReducer.actions'
import { ROOT_NODE_ID } from '../tenancy/constants/TenancyConstants'
import { getVarValue } from '../common/utils/Sparky'

// Constants
import { FF_DEFAULT_TSG } from '../common/utils/FeatureFlags'
import AuthContext from '../common/context/AuthContext'

// Feature Toggles
const enableDefaultTSG = getVarValue(FF_DEFAULT_TSG)

const LoadAllowedIps = (tsg_id: string) => IAMService.ipRestrictionPolicy.get(tsg_id).then(({ data }) => data)

const Page: VFC<any> = () => {

  const { auth } = useContext(AuthContext)
  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { clearBanner, apiBannerHandler } = useMessage()
  const { setLoading, loading } = useLoadingContext()
  const tsgRef = useRef<Tsg | null>(null)
  const { checkIpRestricted, setRestricted } = useIpRestrictionContext()

  const getAllowedIps = useCallback(async (tsg_id, dispatch) => {
    if (tsg_id === ROOT_NODE_ID) {
      dispatch(setAllowedIps([]))
      return
    }
    setRestricted(false)
    setLoading(true)
    dispatch(setAllowedIps([]))
    dispatch(setTrustedIPLimit(20))
    try {
      const trustedIPConfiguration = await LoadAllowedIps(tsg_id)

      const allowedIps = trustedIPConfiguration.ip_addresses
      const trustedIPLimit = trustedIPConfiguration.limit

      if (tsgRef.current?.id === tsg_id) {
        dispatch(setAllowedIps(allowedIps))
        dispatch(setTrustedIPLimit(trustedIPLimit))
      }
    }
    catch (error) {
      if (tsgRef.current?.id === tsg_id) {
        if (!checkIpRestricted(error)) {
          apiBannerHandler(error)
        }
        dispatch(setAllowedIps([]))
        dispatch(setTrustedIPLimit(20))
      }
    }
    setLoading(false)
  }, [apiBannerHandler, checkIpRestricted, setLoading, setRestricted])

  useEffect(() => {
    tsgRef.current = selectedTreeNode
    const tsg_id = selectedTreeNode?.id
    if (tsg_id) {
      getAllowedIps(tsg_id, dispatch)
    }
  }, [dispatch, getAllowedIps, selectedTreeNode])

  useEffect(() => {
    return clearBanner
  }, [clearBanner])

  const showSidebar = useMemo(() => {
    if (enableDefaultTSG) {
      return auth?.tsgs?.hasMsp
    }
    return true
  }, [auth?.tsgs?.hasMsp])

  if (selectedTreeNode?.id === ROOT_NODE_ID) {
    return (
      <div className='cm-relative cm-w-full cm-h-full cm-overflow-auto cm-flex cm-flex-col'>
        <NoDataScreen />
      </div>
    )
  }
  return (
    <div className='cm-flex cm-flex-row cm-h-full cm-w-full'>
      {showSidebar && (
        <div className="common-module-side-bar dark:cm-bg-gray-950">
          <RCTree
            {...{ ...treeReducer, dispatch }}
            expandedKeys={[selectedTreeNode?.parent_id]}
          />
        </div>
      )}
      <IPRestrictionWrapper>
        <div
          className='cm-relative cm-w-full cm-h-full cm-overflow-auto cm-flex cm-flex-col'
        >
          <IpAllowBanner/>
          <TrustedIpForm {...{ getAllowedIps, loading }} />
        </div>
      </IPRestrictionWrapper>
    </div>
  )
}

const IpAllowList = (props = {}) => (
  <IpRestrictProvider>
    <LoadingProvider>
      <ToastProvider>
        <Page {...props}/>
      </ToastProvider>
    </LoadingProvider>
  </IpRestrictProvider>
)

export default IpAllowList
